import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const readLS = (k, def) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? def; } catch { return def; }
};

export const StoreProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined=loading, null=guest
  const [settings, setSettings] = useState({});
  const [social, setSocial] = useState({});
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState(() => readLS("anuswa_cart", []));
  const [wishlist, setWishlist] = useState(() => readLS("anuswa_wishlist", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/social").then((r) => setSocial(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    const token = localStorage.getItem("anuswa_token");
    if (token) {
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem("anuswa_token"); setUser(null); });
    } else setUser(null);
  }, []);

  useEffect(() => { localStorage.setItem("anuswa_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("anuswa_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  const loginSuccess = (data) => {
    localStorage.setItem("anuswa_token", data.token);
    setUser(data.user);
  };

  const logout = () => {
    localStorage.removeItem("anuswa_token");
    setUser(null);
    toast.success("Logged out");
  };

  const addToCart = (product, qty = 1, size = "", color = "") => {
    setCart((prev) => {
      const key = `${product.id}-${size}-${color}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + qty } : i));
      }
      return [...prev, {
        key, product_id: product.id, name: product.name, slug: product.slug,
        image: product.primary_image, price: product.price, mrp: product.mrp,
        quantity: qty, size, color,
      }];
    });
    toast.success(`${product.name} added to cart`);
    setCartOpen(true);
  };

  const updateQty = (key, qty) => {
    if (qty < 1) return removeFromCart(key);
    setCart((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: qty } : i)));
  };
  const removeFromCart = (key) => setCart((prev) => prev.filter((i) => i.key !== key));
  const clearCart = () => setCart([]);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      if (prev.find((p) => p.id === product.id)) {
        toast.info("Removed from wishlist");
        return prev.filter((p) => p.id !== product.id);
      }
      toast.success("Added to wishlist");
      return [...prev, { id: product.id, name: product.name, slug: product.slug, primary_image: product.primary_image, price: product.price, mrp: product.mrp, category: product.category, rating: product.rating }];
    });
  };
  const inWishlist = (id) => wishlist.some((p) => p.id === id);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const value = {
    user, setUser, loginSuccess, logout, settings, social, categories,
    cart, cartOpen, setCartOpen, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartSubtotal,
    wishlist, toggleWishlist, inWishlist,
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
